import type { NumericPoint } from "@incytestudios/ddv";

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

const collapseDuplicateXPoints = (points: NumericPoint[]): NumericPoint[] => {
  if (points.length < 2) return points;

  const grouped = new Map<string, { x: number; ys: number[] }>();
  for (const point of points) {
    const key = point.x.toFixed(6);
    const existing = grouped.get(key);
    if (existing) {
      existing.ys.push(point.y);
    } else {
      grouped.set(key, { x: point.x, ys: [point.y] });
    }
  }

  return Array.from(grouped.values())
    .map(group => ({
      x: group.x,
      // Median is robust against one bad value among duplicates.
      y: median(group.ys)
    }))
    .sort((a, b) => a.x - b.x);
};

// AI generated algorithm to identify and remove outliers from required TDH points
// based on local interpolation and robust z-score. This is due to possibly not having
// a minimum boundry on required TDH points which can lead to extreme outliers that
// skew the chart view. The minimum boundary was removed at some point to display more points.
const filterRequiredTDHOutliers = (points: NumericPoint[]): NumericPoint[] => {
  if (points.length < 3) return points;

  const sorted = [...points].sort((a, b) => a.x - b.x);
  const ys = sorted.map(p => p.y);
  const med = median(ys);
  const mad = median(ys.map(y => Math.abs(y - med)));

  return sorted.filter((p, i, arr) => {
    if (i === 0 || i === arr.length - 1) return true;

    const prev = arr[i - 1];
    const next = arr[i + 1];
    const dx = next.x - prev.x;
    if (dx === 0) return true;

    // Local interpolation helps identify isolated vertical spikes.
    const t = (p.x - prev.x) / dx;
    const expectedY = prev.y + t * (next.y - prev.y);
    const robustZ = mad === 0 ? 0 : Math.abs((0.6745 * (p.y - med)) / mad);

    const baseline = Math.max(Math.abs(expectedY), 1);
    const isHighSpike =
      p.y > expectedY + baseline * 1.5 &&
      p.y - prev.y > Math.max(mad * 8, baseline * 0.75) &&
      p.y - next.y > Math.max(mad * 8, baseline * 0.75);

    if (mad === 0) {
      return !isHighSpike;
    }

    return !(robustZ > 6 && isHighSpike);
  });
};

export const normalizeRequiredTDHPoints = (
  points: NumericPoint[]
): NumericPoint[] => {
  const deduplicated = collapseDuplicateXPoints(points);
  return filterRequiredTDHOutliers(deduplicated);
};
